import React, { useCallback, useEffect, useState } from 'react';
import { FiDownload, FiRefreshCw, FiTarget } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { formatMoney } from '../utils/money';

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function ProgressBar({ ratio, modoOscuro }) {
  if (ratio == null) return null;
  const pct = Math.round(ratio * 100);
  return (
    <div className={`h-1.5 rounded-full overflow-hidden mt-1 ${modoOscuro ? 'bg-gray-700' : 'bg-gray-200'}`}>
      <div
        className="h-full rounded-full bg-[#1F80FF] transition-all"
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  );
}

export default function PlatformLedgerPanel({ modoOscuro, variant = 'inicio' }) {
  const { secureFetch } = useApi();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [goalsDraft, setGoalsDraft] = useState({ mrrArs: '', paidClients: '', resultadoMesArs: '' });
  const [savingGoals, setSavingGoals] = useState(false);
  const [syncingMp, setSyncingMp] = useState(false);
  const [syncingClients, setSyncingClients] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const textPri = modoOscuro ? 'text-gray-100' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const box = modoOscuro ? 'bg-gray-900/80 border-gray-800' : 'bg-white border-gray-200';

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await secureFetch('/api/platform-ledger/dashboard');
      if (!res.ok) throw new Error('No se pudieron cargar las métricas del libro.');
      const data = await res.json();
      setDashboard(data);
      const g = data.goals || {};
      setGoalsDraft({
        mrrArs: g.mrrArs != null ? String(g.mrrArs) : '',
        paidClients: g.paidClients != null ? String(g.paidClients) : '',
        resultadoMesArs: g.resultadoMesArs != null ? String(g.resultadoMesArs) : '',
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const saveGoals = async () => {
    setSavingGoals(true);
    setMessage({ type: '', text: '' });
    try {
      const body = {
        mrrArs: goalsDraft.mrrArs === '' ? null : Number(goalsDraft.mrrArs),
        paidClients: goalsDraft.paidClients === '' ? null : Number(goalsDraft.paidClients),
        resultadoMesArs: goalsDraft.resultadoMesArs === '' ? null : Number(goalsDraft.resultadoMesArs),
      };
      const res = await secureFetch('/api/platform-ledger/goals', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Error al guardar metas.');
      }
      setMessage({ type: 'success', text: 'Metas guardadas.' });
      await loadDashboard();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSavingGoals(false);
    }
  };

  const resyncSubscribers = async () => {
    setSyncingClients(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await secureFetch('/api/platform-ledger/resync-subscribers', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Error al actualizar clientes.');
      setMessage({
        type: 'success',
        text: `Clientes actualizados: ${data.stats?.clientsUpdated ?? 0} (nuevos: ${data.stats?.clientsCreated ?? 0}).`,
      });
      await loadDashboard();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSyncingClients(false);
    }
  };

  const syncMp = async () => {
    setSyncingMp(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await secureFetch('/api/platform-ledger/sync-mercadopago-payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthsBack: 6 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Error al importar cobros MP.');
      const created = data.stats?.mercadopago?.revenuesCreated ?? 0;
      const skipped = data.stats?.mercadopago?.revenuesSkipped ?? 0;
      setMessage({
        type: 'success',
        text: `Importación lista. Ventas nuevas: ${created}, omitidas: ${skipped}.`,
      });
      await loadDashboard();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSyncingMp(false);
    }
  };

  const exportPnl = async () => {
    const now = new Date();
    const desde = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const hasta = formatLocalDate(now);
    try {
      const res = await secureFetch(`/api/platform-ledger/export/pnl.csv?desde=${desde}&hasta=${hasta}`);
      if (!res.ok) throw new Error('No se pudo exportar el CSV.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contsuite-pyl-${desde}_${hasta}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  if (loading && !dashboard) {
    return (
      <div className={`rounded-xl border p-4 ${box}`}>
        <p className={`text-sm ${textMuted}`}>Cargando libro Contsuite…</p>
      </div>
    );
  }

  if (!dashboard) return null;

  const inputClass = `w-full rounded-md border px-2 py-1.5 text-sm ${
    modoOscuro ? 'bg-gray-800 border-gray-700 text-gray-100' : 'bg-white border-gray-300'
  }`;

  return (
    <section className={`rounded-xl border p-4 md:p-5 ${box}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className={`text-base font-semibold flex items-center gap-2 ${textPri}`}>
            <FiTarget className="text-[#1F80FF]" />
            Negocio Contsuite
          </h2>
          <p className={`text-xs mt-0.5 ${textMuted}`}>
            Mes {dashboard.month} · ingresos plataforma, egresos manuales y metas.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {variant === 'cuenta' && (
            <>
              <button
                type="button"
                onClick={resyncSubscribers}
                disabled={syncingClients}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border disabled:opacity-60 ${
                  modoOscuro ? 'border-gray-600 text-gray-200' : 'border-gray-300 text-gray-700'
                }`}
              >
                <FiRefreshCw className={syncingClients ? 'animate-spin' : ''} />
                Actualizar clientes
              </button>
              <button
                type="button"
                onClick={syncMp}
                disabled={syncingMp}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#1F80FF] text-white disabled:opacity-60"
              >
                <FiRefreshCw className={syncingMp ? 'animate-spin' : ''} />
                Importar cobros MP
              </button>
            </>
          )}
          <button
            type="button"
            onClick={exportPnl}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${
              modoOscuro ? 'border-gray-600 text-gray-200' : 'border-gray-300 text-gray-700'
            }`}
          >
            <FiDownload />
            Export P&L CSV
          </button>
        </div>
      </div>

      {message.text && (
        <p
          className={`text-xs mb-3 ${
            message.type === 'error' ? 'text-red-500' : 'text-emerald-600'
          }`}
        >
          {message.text}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className={`rounded-lg p-3 ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
          <p className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Ingresos plataforma</p>
          <p className={`text-lg font-semibold ${textPri}`}>{formatMoney(dashboard.ingresosMes, 'ARS')}</p>
          <ProgressBar ratio={dashboard.progress?.mrr} modoOscuro={modoOscuro} />
        </div>
        <div className={`rounded-lg p-3 ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
          <p className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Clientes activos</p>
          <p className={`text-lg font-semibold ${textPri}`}>
            {dashboard.cartera?.paid ?? 0}
            <span className={`text-sm font-normal ${textMuted}`}> (+{dashboard.cartera?.trial ?? 0} prueba)</span>
          </p>
          <ProgressBar ratio={dashboard.progress?.paidClients} modoOscuro={modoOscuro} />
        </div>
        <div className={`rounded-lg p-3 ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
          <p className={`text-[10px] uppercase tracking-wide ${textMuted}`}>Resultado mes</p>
          <p className={`text-lg font-semibold ${textPri}`}>{formatMoney(dashboard.resultadoMes, 'ARS')}</p>
          <ProgressBar ratio={dashboard.progress?.resultado} modoOscuro={modoOscuro} />
        </div>
      </div>

      {variant === 'inicio' && (
        <div className="space-y-2">
          <p className={`text-xs font-semibold ${textPri}`}>Metas del mes</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className={`text-[11px] ${textMuted}`}>
              Ingresos objetivo (ARS)
              <input
                className={`${inputClass} mt-0.5`}
                value={goalsDraft.mrrArs}
                onChange={(e) => setGoalsDraft((g) => ({ ...g, mrrArs: e.target.value }))}
                inputMode="decimal"
              />
            </label>
            <label className={`text-[11px] ${textMuted}`}>
              Clientes pagos
              <input
                className={`${inputClass} mt-0.5`}
                value={goalsDraft.paidClients}
                onChange={(e) => setGoalsDraft((g) => ({ ...g, paidClients: e.target.value }))}
                inputMode="numeric"
              />
            </label>
            <label className={`text-[11px] ${textMuted}`}>
              Resultado objetivo (ARS)
              <input
                className={`${inputClass} mt-0.5`}
                value={goalsDraft.resultadoMesArs}
                onChange={(e) => setGoalsDraft((g) => ({ ...g, resultadoMesArs: e.target.value }))}
                inputMode="decimal"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={saveGoals}
            disabled={savingGoals}
            className="mt-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#1F80FF] text-white disabled:opacity-60"
          >
            {savingGoals ? 'Guardando…' : 'Guardar metas'}
          </button>
        </div>
      )}
    </section>
  );
}
